import * as Sentry from "@sentry/nextjs";
import { useQuery, keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "@/app/providers/AxiosProvider";
import handleError from "@lib/helpers/handleError";
import buildListUrl from "@lib/shared/queryString";

export type TagRelation = "candidates" | "contacts";

export interface Tag {
  id: number;
  name: string;
  slug: string;
  candidates_count?: number;
  contacts_count?: number;
  candidates?: Record<string, unknown>[];
  contacts?: Record<string, unknown>[];
}

export interface UseTagsOptions {
  page?: number;
  sortBy?: { property: string; direction: "asc" | "desc" };
  search?: string;
  filters?: Record<string, string | number | boolean | string[] | undefined>;
  /**
   * Tagged records to embed on each tag. Defaults to none: every tag still
   * carries `candidates_count` / `contacts_count`, and embedding the records
   * themselves made /tags run to megabytes for large accounts.
   */
  include?: TagRelation[];
}

const DEFAULT_SORT = {
  property: "updated_at",
  direction: "desc",
} as const;

function reportUnexpectedBody(
  url: string,
  response: { status: number; headers: Record<string, unknown>; data: unknown }
) {
  const body = response.data;
  const text = typeof body === "string" ? body : JSON.stringify(body) ?? String(body);

  Sentry.captureMessage("Unexpected /tags response body", {
    level: "warning",
    extra: {
      url,
      status: response.status,
      contentType: response.headers?.["content-type"],
      contentLength: response.headers?.["content-length"],
      bodyType: body === null ? "null" : typeof body,
      bodyLength: text.length,
      bodyStart: text.slice(0, 200),
      bodyEnd: text.slice(-200),
    },
  });
}

export default function useTags({
  page = 1,
  sortBy = DEFAULT_SORT,
  search = "",
  filters = {},
  include = [],
}: UseTagsOptions) {
  const api = useAxios();
  const client = useQueryClient();

  const url = buildListUrl(
    "/tags",
    {
      page,
      sortBy: sortBy.property,
      sortDirection: sortBy.direction,
      search,
      include: include.join(","),
    },
    filters
  );

  // note isFetching is available to show a loading indicator while loading new data
  const { isPending, error, data } = useQuery({
    // The URL is the only input the queryFn reads, so keying on it covers every
    // page/sort/search/filter combination — otherwise staleTime would serve a
    // cached page for different arguments and pagination would appear broken.
    queryKey: ["tags", url],
    queryFn: async (): Promise<Tag[]> => {
      const response = await api.get(url);
      if (Array.isArray(response.data)) return response.data;

      // axios hands back the raw text when a body fails to parse as JSON, so a
      // truncated or proxy-rewritten response arrives here as a string and used
      // to crash every `.map`/`.filter` over the tags. Report what arrived.
      reportUnexpectedBody(url, response);
      return [];
    },
    placeholderData: keepPreviousData,
    // Tags change infrequently and are fetched on many pages; cache briefly to
    // avoid refetching the (relatively expensive) /tags endpoint each navigation.
    staleTime: 2 * 60_000,
  });

  handleError(error);

  const invalidate = () => {
    client.invalidateQueries({ queryKey: ["tags"] });
  };

  const tags: Tag[] = data ?? [];

  return { tags, isLoading: isPending, invalidate };
}
